import type { OnboardingData, QuestionnaireAnswerPayload } from "../types";

type QuestionCode =
  | "nb_1"
  | "nb_2"
  | "nb_5"
  | "nb_7"
  | "nb_8"
  | "nb_9"
  | "pc_1"
  | "pc_2"
  | "pc_4"
  | "pc_7"
  | "int_1"
  | "int_2"
  | "int_3"
  | "int_4"
  | "int_5"
  | "int_6"
  | "int_7"
  | "int_8"
  | "int_9"
  | "ea_2"
  | "ea_3"
  | "fl_2"
  | "fl_3"
  | "fl_7"
  | "fl_9"
  | "fl_11"
  | "ctx_1"
  | "ctx_2"
  | "ctx_4"
  | "adh_1"
  | "adh_3"
  | "adh_4"
  | "obj_1"
  | "obj_3";

type AnswerValue = string | number | string[];

const SHORT_QUESTION_IDS: Record<QuestionCode, number> = {
  nb_1: 61,
  nb_2: 62,
  nb_5: 63,
  nb_7: 64,
  nb_8: 65,
  nb_9: 66,
  pc_1: 67,
  pc_2: 68,
  pc_4: 69,
  pc_7: 70,
  int_1: 71,
  int_2: 72,
  int_3: 73,
  int_4: 74,
  int_5: 75,
  int_6: 76,
  int_7: 77,
  int_8: 78,
  int_9: 79,
  ea_2: 80,
  ea_3: 81,
  fl_2: 82,
  fl_3: 83,
  fl_7: 84,
  fl_9: 85,
  fl_11: 86,
  ctx_1: 87,
  ctx_2: 88,
  ctx_4: 89,
  adh_1: 90,
  adh_3: 91,
  adh_4: 92,
  obj_1: 93,
  obj_3: 94,
};

const DEFAULT_SHORT_ANSWERS: Record<QuestionCode, AnswerValue> = {
  nb_1: "Manhã",
  nb_2: "Razoável",
  nb_5: "30–60min",
  nb_7: "Silêncio total",
  nb_8: ["Pensamentos internos"],
  nb_9: "Planejo antes",
  pc_1: "Alternar",
  pc_2: "Equilíbrio",
  pc_4: "Planejo antes",
  pc_7: "Busca outra forma",
  int_1: 3,
  int_2: 3,
  int_3: 3,
  int_4: 3,
  int_5: 3,
  int_6: 3,
  int_7: 3,
  int_8: 3,
  int_9: 3,
  ea_2: "Visual",
  ea_3: "Vê",
  fl_2: "Às vezes",
  fl_3: "Às vezes",
  fl_7: "Às vezes",
  fl_9: "Às vezes",
  fl_11: "Às vezes",
  ctx_1: "Estudo e trabalho",
  ctx_2: "1–2h",
  ctx_4: "Afeta pouco",
  adh_1: "Parcialmente",
  adh_3: "Interesse",
  adh_4: "Às vezes",
  obj_1: "Aprender algo específico",
  obj_3: "Médio",
};

function mapStudyPeriod(value: OnboardingData["bestStudyPeriod"]): string {
  if (value === "morning") return "Manhã";
  if (value === "afternoon") return "Tarde";
  return "Noite";
}

function mapFocusDuration(value: number): string {
  if (value <= 1) return "<15min";
  if (value <= 2) return "15–30min";
  if (value <= 3) return "30–60min";
  return "60+min";
}

function mapStudyPreference(value: OnboardingData["studyPreference"]): string {
  if (value === "solo") return "Silêncio total";
  if (value === "group") return "Ambiente movimentado";
  return "Ruído leve";
}

function mapVarkToOption(value: OnboardingData["varkPrimary"]): string {
  if (value === "visual") return "Visual";
  if (value === "auditory") return "Áudio";
  if (value === "reading") return "Leitura";
  return "Prática";
}

function mapGoals(goals: string[]): string {
  const firstGoal = goals[0]?.toLowerCase() ?? "";
  if (firstGoal.includes("vestibular") || firstGoal.includes("concurso")) {
    return "Desempenho geral";
  }
  if (firstGoal.includes("profission")) {
    return "Carreira";
  }
  if (firstGoal.includes("interesse")) {
    return "Explorar";
  }
  return "Aprender algo específico";
}

function mapChallengeToFrequency(challenges: string[]): string {
  const joined = challenges.join(" ").toLowerCase();
  if (joined.includes("dificuld") || joined.includes("ansiedade")) {
    return "Frequentemente";
  }
  return "Às vezes";
}

export function mapOnboardingToShortQuestionnaireAnswers(
  onboardingData: OnboardingData,
): QuestionnaireAnswerPayload[] {
  const overrides: Partial<Record<QuestionCode, AnswerValue>> = {
    nb_1: mapStudyPeriod(onboardingData.bestStudyPeriod),
    nb_5: mapFocusDuration(onboardingData.availableHoursPerDay),
    nb_7: mapStudyPreference(onboardingData.studyPreference),
    ea_2: mapVarkToOption(onboardingData.varkPrimary),
    ea_3: onboardingData.varkPrimary === "kinesthetic" ? "Executa" : DEFAULT_SHORT_ANSWERS.ea_3,
    obj_1: mapGoals(onboardingData.goals),
    fl_11: mapChallengeToFrequency(onboardingData.challenges),
    nb_8:
      onboardingData.challenges.length > 0
        ? onboardingData.challenges.slice(0, 2)
        : DEFAULT_SHORT_ANSWERS.nb_8,
    int_1: onboardingData.multipleIntelligences.includes("logical") ? 5 : 3,
    int_2: onboardingData.multipleIntelligences.includes("linguistic") ? 5 : 3,
    int_3: onboardingData.multipleIntelligences.includes("spatial") ? 5 : 3,
    int_4: onboardingData.multipleIntelligences.includes("bodily") ? 5 : 3,
    int_5: onboardingData.multipleIntelligences.includes("musical") ? 5 : 3,
    int_6: onboardingData.multipleIntelligences.includes("interpersonal") ? 5 : 3,
    int_7: onboardingData.multipleIntelligences.includes("intrapersonal") ? 5 : 3,
    int_8: onboardingData.multipleIntelligences.includes("naturalist") ? 5 : 3,
    int_9: onboardingData.multipleIntelligences.length >= 3 ? 4 : 3,
  };

  return (Object.keys(SHORT_QUESTION_IDS) as QuestionCode[]).map((code) => ({
    question_id: SHORT_QUESTION_IDS[code],
    answer_value: overrides[code] ?? DEFAULT_SHORT_ANSWERS[code],
  }));
}

export const QUESTIONNAIRE_VERSION = "short";
